import { useSearchParams } from 'react-router-dom';
import { FanTriviaEngagement } from '../fan/fan-view';
import { TriviaAdminControlView } from '../admin/control-view';
import { TRIVIA_PREVIEW_FRAMES, TriviaPreviewFrame } from './fixtures';
import { TriviaEditorView } from '../admin/editor-view';
import { AdminEngagementProvider } from '@/providers/AdminEngagementProvider/AdminEngagementProvider';
import { FullEngagementFragment, FullEventFragment } from '@/generated/graphql';
import { useEffect, useMemo } from 'react';
import {
  TriviaPreviewTheme,
  TriviaStagePreviewShell,
} from './TriviaStagePreviewShell';

export default function TriviaPreviewPage() {
  const [params] = useSearchParams();
  const frame = (params.get('frame') || 'stage-idle') as TriviaPreviewFrame;
  const titleOverride = params.get('title');
  const theme: TriviaPreviewTheme =
    params.get('theme') === 'light' ? 'light' : 'glow';
  const surface = params.get('frame') === 'stage-slides' ? 'slides' : 'trivia';
  const engagement = useMemo(() => {
    const base =
      TRIVIA_PREVIEW_FRAMES[frame] ?? TRIVIA_PREVIEW_FRAMES['stage-idle'];
    if (!titleOverride) return base;
    return { ...base, title: titleOverride };
  }, [frame, titleOverride]);
  const isFan = frame.startsWith('fan-');
  const isControl = frame.startsWith('control-');
  const isConfig = frame.startsWith('config-');
  const isStage = !isFan && !isControl && !isConfig;
  const height = Number(params.get('h')) || undefined;
  const requestedWidth = Number(params.get('w'));
  const width =
    Number.isFinite(requestedWidth) && requestedWidth > 0
      ? requestedWidth
      : isFan || (isControl && params.get('mobile') === '1')
        ? 390
        : isControl || isConfig
          ? 1440
          : 1920;

  const event = useMemo(
    () =>
      ({
        id: 'preview',
        name: 'Preview',
        engagements: [engagement],
        activeEngagement: engagement,
      }) as unknown as FullEventFragment,
    [engagement],
  );

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <div
      data-trivia-preview={frame}
      data-preview-theme={theme}
      style={{
        width,
        height:
          height ??
          (isFan || params.get('mobile') === '1'
            ? 844
            : isControl
              ? 960
              : isConfig
                ? 'auto'
                : 1080),
        minHeight: isConfig ? 960 : undefined,
        background: isStage ? 'transparent' : '#000',
        overflow: isConfig ? 'visible' : 'hidden',
      }}
    >
      {isConfig ? (
        <AdminEngagementProvider
          event={event}
          engagement={engagement as unknown as FullEngagementFragment}
        >
          <div className="h-full overflow-auto bg-background p-8 text-foreground">
            <TriviaEditorView />
          </div>
        </AdminEngagementProvider>
      ) : isControl ? (
        <TriviaAdminControlView
          engagement={engagement as unknown as FullEngagementFragment}
        />
      ) : isFan ? (
        <FanTriviaEngagement engagement={engagement as never} />
      ) : (
        <TriviaStagePreviewShell
          engagement={engagement as never}
          theme={theme}
          surface={surface}
        />
      )}
    </div>
  );
}
